import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ApiFragment } from '../../../entities/api-fragment.entity';
import { FormatBadge, StukFormats } from '../../../entities/stuk-formats.entity';
import { toFormatBadges } from '../../../helpers/format-badges.helper';
import { BaseColumnComponent } from '../../../services/base-column-component';
import { StukFormatsService } from '../../../services/stuk-formats.service';

/**
 * HFBD-7a: the `Formaten` column — one tag per distinct format the row exists in.
 *
 * HFBD-75: which is derived two ways, because the column set is shown at stuk level *and* on a flat page that
 * has been narrowed to stuk-level records. A **stuk**'s formats live on its representaties, so the cell fetches
 * nothing itself: {@link StukFormatsService} answers for a whole expansion — or a whole flat page — in one
 * request and this reads the grouped result, so 25 rows still cost one lookup rather than 25. A
 * **weesdocument** takes the same route, for the same reason — see {@link ApiFragment.hasRepresentationFormats},
 * which is the predicate both this cell and the page's batched lookup read, so neither can start asking about
 * rows the other does not.
 *
 * Anything else answers from its own record, at no request cost. A **representatie** *is* exactly one format,
 * and carries its roles on the same record, so it yields a badge of identical shape. Pointing the batched
 * lookup at it instead would return a *wrong* answer rather than a missing one: it groups representaties by
 * their parent stuk, finds none for a representatie, and settles on "ready, no formats" — a dash where a real
 * format exists. Any other type (a *Serie* reached through the empty-under-tree fallback) derives nothing and
 * lands on the dash, rather than on a skeleton no lookup would ever resolve.
 */
@Component({
    standalone: false,
    selector: 'digihaven-stuk-formats-cell',
    templateUrl: './stuk-formats-cell.component.html',
    styleUrls: ['./stuk-formats-cell.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StukFormatsCellComponent extends BaseColumnComponent<ApiFragment> {
    formats$: Observable<StukFormats>;

    constructor(
        formatsService: StukFormatsService,
        translate: TranslateService,
        cd: ChangeDetectorRef
    ) {
        super(cd);

        this.formats$ = this.data$.pipe(
            switchMap(record =>
                record.hasRepresentationFormats()
                    ? formatsService
                          .formatsFor(record.getId())
                          .pipe(map(formats => StukFormatsCellComponent.withOwnFormat(formats, record, translate)))
                    : of<StukFormats>({ status: 'ready', badges: toFormatBadges([record], translate), previewRecordId: null })
            )
        );
    }

    private static withOwnFormat(formats: StukFormats, record: ApiFragment, translate: TranslateService): StukFormats {
        if (formats.status === 'loading' || formats.badges.length > 0) {
            return formats;
        }

        const ownBadges: FormatBadge[] = toFormatBadges([record], translate);

        return ownBadges.length > 0 ? { ...formats, status: 'ready', badges: ownBadges } : formats;
    }
}
